/**
 * Routes and guards.
 *
 * Two layouts hang off the root. The app layout wears `AppShell` with its bottom navigation and
 * requires a session; the auth layout is a bare centred column and requires the opposite, so a
 * signed-in user who opens the sign-in page is sent to the app rather than shown a form they do
 * not need.
 *
 * Both guards read `/me` through the query client in `beforeLoad`, so the decision is made before
 * anything renders and there is no flash of the wrong screen. The answer is cached, so navigating
 * between routes does not re-request it.
 */

import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { Outlet } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { ProductsPage } from "@/features/catalog/ProductsPage";
import { StorePage } from "@/features/catalog/StorePage";
import { StoresPage } from "@/features/catalog/StoresPage";
import { HealthPage } from "@/features/health/HealthPage";
import { CustomersPage } from "@/features/orders/CustomersPage";
import { NewOrderPage } from "@/features/orders/NewOrderPage";
import { OrderPage } from "@/features/orders/OrderPage";
import { OrdersPage } from "@/features/orders/OrdersPage";
import { CapturePage } from "@/features/receipts/CapturePage";
import { ManualReceiptPage } from "@/features/receipts/ManualReceiptPage";
import { ReceiptPage } from "@/features/receipts/ReceiptPage";
import { ReceiptsPage } from "@/features/receipts/ReceiptsPage";
import { ExportPage } from "@/features/reports/ExportPage";
import { parseSearch } from "@/features/reports/filters";
import { ReportsPage } from "@/features/reports/ReportsPage";
import { BillingPage } from "@/features/billing/BillingPage";
import { DataPage } from "@/features/settings/DataPage";
import { CloseAccountPage } from "@/features/settings/CloseAccountPage";
import { AccountClosedPage } from "@/features/auth/AccountClosedPage";
import { parseBillingSearch } from "@/features/billing/search";
import { BusinessesPage } from "@/features/settings/BusinessesPage";
import { CategoriesPage } from "@/features/settings/CategoriesPage";
import { SettingsPage } from "@/features/settings/SettingsPage";
import { WorkspaceSettingsPage } from "@/features/settings/WorkspaceSettingsPage";
import { AuthLayout } from "@/features/auth/AuthLayout";
import { ForgotPasswordPage } from "@/features/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "@/features/auth/ResetPasswordPage";
import { SignInPage } from "@/features/auth/SignInPage";
import { SignUpPage } from "@/features/auth/SignUpPage";
import { VerifyEmailPage } from "@/features/auth/VerifyEmailPage";
import { authConfigQuery, meQuery, type Me } from "@/features/auth/session";

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: Outlet });

/** The signed-in user, or `null`. A failure to reach the API is treated as "not signed in". */
async function currentUser(queryClient: QueryClient): Promise<Me | null> {
  try {
    return await queryClient.ensureQueryData(meQuery);
  } catch {
    return null;
  }
}

// --- the application, behind a session ---------------------------------------------------------

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  component: AppShell,
  beforeLoad: async ({ context, location }) => {
    if (await currentUser(context.queryClient)) return;
    throw redirect({
      to: "/auth/sign-in",
      // Come back here once signed in.
      search: { redirect: location.href },
    });
  },
});

// The Receipts tab is the app's front door, and lives at its own path so the tab stays
// highlighted on every receipt screen beneath it.
const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/receipts" });
  },
});
const receiptsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/receipts",
  component: ReceiptsPage,
});
const captureRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/receipts/new",
  component: CapturePage,
});
const manualReceiptRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/receipts/manual",
  component: ManualReceiptPage,
});
const receiptRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/receipts/$receiptId",
  component: ReceiptPage,
});
// The Milestone 0 status page, kept reachable for checking a deployment by hand.
const healthRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/health",
  component: HealthPage,
});
const ordersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orders",
  component: OrdersPage,
});
const newOrderRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orders/new",
  component: NewOrderPage,
});
const customersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orders/customers",
  component: CustomersPage,
});
const orderRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orders/$orderId",
  component: OrderPage,
});
const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports",
  component: ReportsPage,
  validateSearch: parseSearch,
});
const exportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/export",
  component: ExportPage,
});
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings",
  component: SettingsPage,
});
const workspaceSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/workspace",
  component: WorkspaceSettingsPage,
});
const businessesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/businesses",
  component: BusinessesPage,
});
const categoriesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/categories",
  component: CategoriesPage,
});
const storesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/stores",
  component: StoresPage,
});
const storeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/stores/$storeId",
  component: StorePage,
});
const closeAccountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/close-account",
  component: CloseAccountPage,
});
const dataRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/data",
  component: DataPage,
});
const billingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/billing",
  component: BillingPage,
  validateSearch: parseBillingSearch,
});
const productsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/products",
  component: ProductsPage,
});

// --- authentication, for people without a session ----------------------------------------------

const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth",
  component: AuthLayout,
  beforeLoad: async ({ context }) => {
    // Every POST below needs Django's CSRF cookie, which this response carries.
    await context.queryClient.ensureQueryData(authConfigQuery).catch(() => undefined);
  },
});

/** Pages a signed-in user has no business seeing; the two key-bearing pages are not among them. */
async function redirectIfSignedIn({ context }: { context: RouterContext }) {
  if (await currentUser(context.queryClient)) throw redirect({ to: "/" });
}

const signInRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/sign-in",
  component: SignInPage,
  beforeLoad: redirectIfSignedIn,
  // `exactOptionalPropertyTypes` is on, so the key is omitted rather than set to undefined.
  validateSearch: (search: Record<string, unknown>): { redirect?: string } =>
    typeof search.redirect === "string" ? { redirect: search.redirect } : {},
});

const signUpRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/sign-up",
  component: SignUpPage,
  beforeLoad: redirectIfSignedIn,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

// Reachable with a session on purpose: confirming an address or setting a password after
// clicking an emailed link must work even if the browser is already signed in.
const verifyEmailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/verify-email/$key",
  component: VerifyEmailPage,
});

const accountClosedRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/closed",
  component: AccountClosedPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/reset-password/$key",
  component: ResetPasswordPage,
});

const routeTree = rootRoute.addChildren([
  appRoute.addChildren([
    indexRoute,
    receiptsRoute,
    captureRoute,
    manualReceiptRoute,
    receiptRoute,
    healthRoute,
    ordersRoute,
    newOrderRoute,
    customersRoute,
    orderRoute,
    reportsRoute,
    exportRoute,
    settingsRoute,
    workspaceSettingsRoute,
    businessesRoute,
    categoriesRoute,
    storesRoute,
    storeRoute,
    productsRoute,
    billingRoute,
    dataRoute,
    closeAccountRoute,
  ]),
  authRoute.addChildren([
    signInRoute,
    signUpRoute,
    forgotPasswordRoute,
    verifyEmailRoute,
    resetPasswordRoute,
    accountClosedRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({ routeTree, defaultPreload: "intent", context: { queryClient } });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
